<template>
  <WdsModal
    ref="modal"
    :show="show"
    :title="$t('Import new freelancer contacts')"
    :z-index="4000"
    data-test="trusted-contacts-bulk-import-modal"
    @cancel="toggleModal"
  >
    <template #default>
      <WdsTabs fitted secondary @update:active-tab-key="changeTab">
        <WdsTab
          :title="$t('Upload .csv file')"
          data-test="trusted-contact-import-upload-tab"
          tab-key="trusted-contact-import-upload-tab"
          class="tw-my-6 tw-mx-6"
        >
          <WdsStack direction-column gap-md>
            <div>{{ $t('Import contacts from .csv or .txt files') }}</div>

            <div>
              <WdsButton primary lg @click="selectFile">
                <WdsStack align-center gap-sm>
                  <WdsIcon name="upload" :size="20" />
                  {{ $t('Upload .csv file') }}
                </WdsStack>
              </WdsButton>
            </div>

            <div>
              <WdsStack direction-column gap-xs>
                <WdsText secondary>{{ $t('Fileformat') }}: .csv</WdsText>
                <WdsText secondary>{{ $t('First column') }} ({{ $t('required') }}): Email</WdsText>
                <WdsText secondary>{{ $t('Second column') }} ({{ $t('required') }}): Name</WdsText>
                <WdsText secondary>
                  {{ $t('Third column') }} ({{ $t('optional') }}):
                  {{ $t('comma separated') }} skills
                </WdsText>
              </WdsStack>
            </div>

            <WdsAlert info icon bordered>
              {{
                $t(
                  'Uploads are limited to 250 contacts per upload, to import a larger number of contacts please split your contact list into multiple files.',
                )
              }}
            </WdsAlert>
          </WdsStack>
        </WdsTab>

        <WdsTab
          :title="$t('Copy/paste from file')"
          data-test="trusted-contact-import-copy-tab"
          tab-key="trusted-contact-import-copy-tab"
          class="tw-my-6 tw-mx-6"
        >
          <WdsStack direction-column gap-md>
            <WdsStack direction-column gap-xs>
              <div>1. {{ $t('Copy columns and rows in your spreadsheet.') }}</div>
              <div>2. {{ $t('Paste your data here.') }}</div>
            </WdsStack>

            <WdsInputArea
              v-model="csvText"
              :placeholder="'john@example.org, John Doe, Designer\njane@example.org, Jane Doe, Illustrator'"
              data-test="trusted-contact-import-copy-input"
            />

            <WdsText secondary>
              {{ $t('Email, name and skills, tab or comma separated') }}
            </WdsText>
          </WdsStack>
        </WdsTab>
      </WdsTabs>
    </template>

    <template #footer>
      <WdsStack justify-space-between>
        <WdsButton secondary data-test="trusted-contact-import-cancel-button" @click="toggleModal">
          {{ $t('Cancel') }}
        </WdsButton>

        <BaseHtmlForm :action="formAction" method="POST" enctype="multipart/form-data">
          <input name="action" value="uploadFile" type="hidden" />

          <input id="csvText" name="csvText" :value="csvText" type="hidden" />
          <input
            id="filepicker"
            ref="filepicker"
            type="file"
            accept="text/csv"
            name="file"
            class="hidden"
            data-test="tc-import-filepicker"
            @change="fileSelected"
          />

          <WdsButton
            primary
            :disabled="formIsValid === false"
            data-test="trusted-contact-import-continue-button"
          >
            {{ $t('Next') }}
          </WdsButton>
        </BaseHtmlForm>
      </WdsStack>
    </template>
  </WdsModal>
</template>

<script setup>
import { defineEmits, defineProps, computed, ref } from 'vue'
import {
  WdsModal,
  WdsStack,
  WdsButton,
  WdsText,
  WdsIcon,
  WdsTabs,
  WdsTab,
  WdsInputArea,
  WdsAlert,
} from '@worksome/wds-components'
import BaseHtmlForm from '@/components/BaseHtmlForm.vue'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'

defineProps({
  show: {
    type: Boolean,
    required: true,
  },
})

const emit = defineEmits(['close'])

const selectedTab = ref('')
const filepicker = ref(null)
const csvText = ref('')
const selectedFile = ref(null)

const toggleModal = () => emit('close')

const changeTab = (tab) => {
  selectedTab.value = tab
}

const formIsValid = computed(() => {
  if (selectedTab.value === 'trusted-contact-import-copy-tab') {
    return csvText.value.length > 0
  }

  return selectedFile.value !== null
})

const selectFile = () => {
  filepicker.value.click()
}

const fileSelected = (e) => {
  const { files } = e.target

  selectedFile.value = files[0] ?? null
}

const formAction = computed(() => laravelNamedRoute(namedRoutes.contacts.import))
</script>
